import {
  BaseType,
  QuestionType,
  SimpleChoice,
  SimpleChoiceFragment,
} from "@/generated/graphql";

import { InteractionKind } from "../question-form/interaction-kind-utils";
import {
  NumericalRange,
  QuestionFormField,
  State,
} from "../question-form/state";
import { QuestionFormError } from "./types";

/** Validate input */
export function validateQuestionFormInput(
  state: State,
  promptDocCharsCount?: number,
  childQuestionIds?: string[]
): QuestionFormError {
  let newErrorState: QuestionFormError | null = {};
  /** NOTE: for question types which are expected to have only one field, use the first field */
  const singleField = state.questionFields[0];
  switch (state.questionType) {
    case QuestionType.Truefalse:
    case QuestionType.Mcq:
      newErrorState = validateChoiceInteractionInput(
        singleField?.interaction.choices ?? [],
        singleField?.response.correctValues ?? []
      );
      break;
    case QuestionType.Matching:
      newErrorState = validateMatchingResponseInput(
        singleField?.response.correctValues ?? [],
        singleField?.interaction.targetSet ?? [],
        singleField?.interaction.sourceSet ?? []
      );
      break;

    case QuestionType.Short:
      newErrorState = validateShortResponseInput(
        singleField?.response.correctValues,
        singleField?.response.matchSimilarity
      );
      break;
    case QuestionType.Numerical:
      newErrorState = validateNumericalResponseInput(
        singleField?.response.baseType || null,
        singleField?.response.correctValues,
        singleField?.response.correctRanges
      );
      break;
    case QuestionType.Blanks:
      newErrorState = validateBlanksResponseInput(state.questionFields);
      break;
    case QuestionType.Sub:
      newErrorState = validateSubQuestionInput(childQuestionIds);
      break;
    case QuestionType.Extended:
      newErrorState = validateExtendResponseInput(
        singleField?.interaction.wordLimit ?? null
      );
      break;
    case QuestionType.Hotspot:
      newErrorState = state.promptImage?.layers?.length
        ? validateHotspotTextChoiceResponseInput(state)
        : validateHotspotResponseInput(state);
      break;
    case QuestionType.Video:
      newErrorState = validateVideoResponseInput(
        singleField?.interaction.readingTime ?? null,
        singleField?.interaction.responseTime ?? null,
        singleField?.interaction.maxAttempts ?? null
      );
      break;
  }
  if (state.questionType !== QuestionType.Section) {
    newErrorState = {
      ...newErrorState,
      ...validatePromptDocInput(
        state.questionType === QuestionType.Overview ? 1 : 20,
        promptDocCharsCount
      ),
    };
  }
  if (
    state.questionType !== QuestionType.Overview &&
    state.questionType !== QuestionType.Section &&
    state.questionType !== QuestionType.Sub
  ) {
    newErrorState = {
      ...newErrorState,
      ...validatePointsInput(state.points),
    };
  }

  return newErrorState ?? {};
}

export const MAX_VIDEO_RESPONSE_TIME_SECONDS = 15 * 60; // 15 minutes
export const MAX_VIDEO_ATTEMPTS = 5;
export const MIN_PREPARATION_TIME_SECONDS = 5;
export const MAX_PREPARATION_TIME_SECONDS = 120;

/** Validation function of video response input. Return null if input is valid. */
export const validateVideoResponseInput = (
  preparationTime: number | null,
  responseTime: number | null,
  maxAttempts: number | null
): QuestionFormError | null => {
  let validateResult: QuestionFormError | null = null;

  if (responseTime === null || responseTime <= 0) {
    validateResult = { ...(validateResult ?? {}), noResponseTime: true };
  } else if (responseTime > MAX_VIDEO_RESPONSE_TIME_SECONDS) {
    validateResult = {
      ...(validateResult ?? {}),
      responseTimeExceedsMax: true,
    };
  }

  if (
    preparationTime === null ||
    preparationTime < MIN_PREPARATION_TIME_SECONDS
  ) {
    validateResult = {
      ...(validateResult ?? {}),
      preparationTimeTooShort: true,
    };
  } else if (preparationTime > MAX_PREPARATION_TIME_SECONDS) {
    validateResult = {
      ...(validateResult ?? {}),
      preparationTimeExceedsMax: true,
    };
  }

  if (maxAttempts === null || maxAttempts <= 0) {
    validateResult = { ...(validateResult ?? {}), noMaxAttempts: true };
  } else if (maxAttempts > MAX_VIDEO_ATTEMPTS) {
    validateResult = {
      ...(validateResult ?? {}),
      maxAttemptsExceedsMax: true,
    };
  }

  return validateResult;
};

/** Validation function of extend response input. Return empty object if input is valid.*/
export const validateExtendResponseInput = (
  wordLimit: number | null
): QuestionFormError | null => {
  if (wordLimit === null) return null;
  if (wordLimit <= 0 || Number.isNaN(wordLimit)) return { noWordLimit: true };
  return null;
};

/** Validation function of boolean response input. Return null if input is valid.*/
export const validateBooleanResponseInput = (
  isTrue?: boolean
): QuestionFormError | null => {
  if (typeof isTrue === "boolean") return null;
  return { noCorrectAnswer: true };
};

/** Validation function of sub question input. Return null if input is valid.*/
export const validateSubQuestionInput = (
  childIds?: string[]
): QuestionFormError | null => {
  if (!childIds || childIds.length === 0) return { noChildQuestion: true };
  return null;
};

/** Validation function of short response input. Return null if input is valid.*/
export const validateShortResponseInput = (
  matchPhrases?: string[],
  matchSimilarity?: number | null
): QuestionFormError | null => {
  let validateResult: QuestionFormError | null = null;
  if (!matchPhrases || matchPhrases.length === 0)
    validateResult = { ...(validateResult ?? {}), noMatchPhrases: true };
  if (
    matchSimilarity === null ||
    matchSimilarity === undefined ||
    Number.isNaN(matchSimilarity)
  ) {
    validateResult = { ...(validateResult ?? {}), noRequiredSimilarity: true };
  } else {
    if (matchSimilarity < 50)
      validateResult = {
        ...(validateResult ?? {}),
        similarityTooSmall: true,
      };
  }
  return validateResult;
};

/** Validation function of short response input. Return null if input is valid.*/
export const validateNumericalResponseInput = (
  baseType: BaseType | null,
  correctValues?: string[],
  correctRanges?: NumericalRange[]
): QuestionFormError | null => {
  let validateResult: QuestionFormError | null = null;

  if (baseType === BaseType.Float) {
    if (!correctValues || correctValues.length === 0)
      validateResult = { ...(validateResult ?? {}), noCorrectAnswer: true };
  }

  if (baseType === BaseType.Range) {
    if (!correctRanges || correctRanges.length === 0)
      validateResult = { ...(validateResult ?? {}), noCorrectAnswer: true };
    else if (
      correctRanges.some((range) => range.start === null || range.end === null)
    ) {
      validateResult = { ...(validateResult ?? {}), hasIncompleteRanges: true };
    }
  }
  return validateResult;
};

/** Validation function for matching response input. */
const validateMatchingResponseInput = (
  correctValues: string[],
  targetSet: SimpleChoice[],
  sourceSet: SimpleChoice[]
): QuestionFormError | null => {
  let validateResult: QuestionFormError | null = null;

  // Check if there is at least one prompt / answer pair
  if (
    !(correctValues?.length > 0) ||
    !(targetSet?.length > 0) ||
    !(sourceSet?.length > 0)
  ) {
    validateResult = { ...(validateResult ?? {}), noCorrectAnswer: true };
  }

  // Prevent duplicate prompts
  const hasDuplicatePrompts = (set: SimpleChoice[]) =>
    set.some(
      (item, index, arr) =>
        arr.findIndex((i) => i.content.trim() === item.content.trim()) !== index
    );

  if (hasDuplicatePrompts(targetSet)) {
    validateResult = { ...validateResult, hasDuplicatePrompts: true };
  }

  // Prevent duplicate source items
  const hasDuplicateSources = (set: SimpleChoice[]) =>
    set.some(
      (item, index, arr) =>
        arr.findIndex((i) => i.content.trim() === item.content.trim()) !== index
    );

  if (hasDuplicateSources(sourceSet)) {
    validateResult = { ...validateResult, hasDuplicateAnswerChoices: true };
  }

  // Ensure that there are no empty prompts or answers
  const hasEmptyPrompts = targetSet.some((item) => item.content.trim() === "");
  const hasEmptyAnswers = sourceSet.some((item) => item.content.trim() === "");

  if (hasEmptyPrompts || hasEmptyAnswers) {
    validateResult = { ...validateResult, hasIncompleteForm: true };
  }

  return validateResult;
};

/** Validation function of blanks response input. Return null if input is valid.*/
export const validateBlanksResponseInput = (
  fields: QuestionFormField[]
): QuestionFormError | null => {
  let validateResult: QuestionFormError | null = null;
  if (fields.length === 0)
    validateResult = {
      ...(validateResult ?? {}),
      noMatchBlanks: true,
    };

  const blankWithoutPhrasesIds: string[] = [];
  fields.map((field) => {
    if (field.response.correctValues.length === 0)
      blankWithoutPhrasesIds.push(field.identifier);
  });
  if (blankWithoutPhrasesIds.length > 0)
    validateResult = {
      ...(validateResult ?? {}),
      blankWithoutPhrasesIds,
    };

  // NOTE: currently similarity is the same for all text entry fields
  const textEntryField = fields.find(
    (f) => f.interaction.kind === InteractionKind.TextEntry
  );

  if (textEntryField) {
    const matchSimilarity = textEntryField.response.matchSimilarity;
    if (
      matchSimilarity === null ||
      matchSimilarity === undefined ||
      Number.isNaN(matchSimilarity)
    ) {
      validateResult = {
        ...(validateResult ?? {}),
        noRequiredSimilarity: true,
      };
    } else {
      if (matchSimilarity < 50) {
        validateResult = {
          ...(validateResult ?? {}),
          similarityTooSmall: true,
        };
      }
    }
  }
  return validateResult;
};

/** Validation function of mcq response input. Return null if input is valid.*/
export const validateChoiceInteractionInput = (
  choices: SimpleChoiceFragment[],
  correctChoices: string[]
): QuestionFormError | null => {
  let validateResult: QuestionFormError | null = null;
  if (!choices || choices.length < 2)
    validateResult = { lessThanTwoOptions: true };
  if (choices) {
    choices.forEach((choice) => {
      if (choice.content.length === 0) {
        const error = {
          id: choice.identifier,
          message: "Answer cannot be left blank",
        };
        validateResult = validateResult ?? {};
        if (validateResult.invalidOptions) {
          validateResult.invalidOptions.push(error);
        } else {
          validateResult.invalidOptions = [error];
        }
      }
    });
    if (correctChoices.length === 0) {
      validateResult = { ...(validateResult ?? {}), noCorrectAnswer: true };
    }
  }
  return validateResult;
};

/** Validation function of point input. Return null if input is valid.*/
export const validatePointsInput = (
  points?: number | null
): QuestionFormError | null => {
  if (!points || Number.isNaN(points)) return { noAssignedPoint: true };
  return null;
};

/**
 * Validation function of promptDoc input. Return null if input is valid.
 * Doesn't return error if chars count is undefined
 */
export const validatePromptDocInput = (
  minDocCharsCount: number,
  promptDocCharsCount?: number
): QuestionFormError | null => {
  if (promptDocCharsCount === undefined) return null;
  if (promptDocCharsCount < minDocCharsCount)
    return { promptDocTooShort: true };
  return null;
};

export const validateHotspotResponseInput = ({
  questionFields,
  promptImage,
}: State): QuestionFormError | null => {
  let validateResult: QuestionFormError | null = null;
  const hotspotLabelErrors: { [key: string]: string } = {};

  questionFields.forEach((field) => {
    if (!field.interaction.label) {
      hotspotLabelErrors[field.identifier] = "Label is missing";
    }
    if (
      !field.response.correctAreas ||
      field.response.correctAreas.length === 0
    ) {
      hotspotLabelErrors[field.identifier] = "Mark the correct area";
    }
  });

  if (Object.keys(hotspotLabelErrors).length > 0) {
    validateResult = {
      ...(validateResult ?? {}),
      hotspotLabelErrors,
    };
  }

  if (questionFields.length === 0) {
    validateResult = {
      ...(validateResult ?? {}),
      noHotspotLabelsMsg: "Add at least one label for the question.",
    };
  }

  if (promptImage === null) {
    validateResult = {
      ...(validateResult ?? {}),
      promptImageMissingMsg: "Upload an image for the question.",
    };
  }

  return validateResult;
};

export const validateHotspotTextChoiceResponseInput = ({
  questionFields,
  promptImage,
}: State): QuestionFormError | null => {
  let validateResult: QuestionFormError | null = null;
  const hotspotLabelErrors: { [key: string]: string } = {};

  questionFields.forEach((field) => {
    if (field.response.correctValues.length === 0) {
      hotspotLabelErrors[field.identifier] = "Add correct answer";
    } else {
      const correctChoiceIdentifier = field.response.correctValues[0];
      field.interaction.choices.forEach((choice) => {
        if (
          choice.identifier === correctChoiceIdentifier &&
          choice.content.length === 0
        ) {
          hotspotLabelErrors[field.identifier] = "Answer cannot be left blank";
        }
      });
    }
  });

  if (Object.keys(hotspotLabelErrors).length > 0) {
    validateResult = {
      ...(validateResult ?? {}),
      hotspotLabelErrors,
    };
  }

  if (questionFields.length === 0) {
    validateResult = {
      ...(validateResult ?? {}),
      noHotspotLabelsMsg: "Add at least one label for the question.",
    };
  }

  if (promptImage === null) {
    validateResult = {
      ...(validateResult ?? {}),
      promptImageMissingMsg: "Upload an image for the question.",
    };
  }

  // NOTE: currently similarity is the same for all text entry fields
  const textEntryField = questionFields.find(
    (f) => f.interaction.kind === InteractionKind.TextEntry
  );

  if (textEntryField) {
    const matchSimilarity = textEntryField.response.matchSimilarity;
    if (
      matchSimilarity === null ||
      matchSimilarity === undefined ||
      Number.isNaN(matchSimilarity)
    ) {
      validateResult = {
        ...(validateResult ?? {}),
        noRequiredSimilarity: true,
      };
    } else {
      if (matchSimilarity < 50) {
        validateResult = {
          ...(validateResult ?? {}),
          similarityTooSmall: true,
        };
      }
    }
  }
  return validateResult;
};
